/**
 * One entry in the drawer stack.
 *
 * `data` is the entity being shown and is deliberately `unknown`: callers pass
 * a Member, a SubKrewe, a Stripe lookup result and more, and the drawer only
 * ever compares its `id` to decide whether the stack should reset.
 */
export interface DrawerContent {
  component: unknown
  data?: unknown
  title?: string
  /** Optional width override for the drawer panel. */
  width?: string
}

/** The drawer only cares about identity, so read just that off `data`. */
function entityId(data: unknown): unknown {
  return (data as { id?: unknown } | null | undefined)?.id
}

export const useDrawer = () => {
  // Drawer state. The initializers need explicit types — inferred, they would
  // be `Ref<null>` and `Ref<never[]>`, which nothing can be assigned to.
  const isDrawerOpen = useState('drawer-open', () => false)
  const drawerTitle = useState('drawer-title', () => '')
  const drawerContent = useState<DrawerContent | null>('drawer-content', () => null)
  const drawerHistory = useState<DrawerContent[]>('drawer-history', () => [])

  // Track whether we're navigating from within the drawer
  const isInternalNavigation = useState('drawer-internal-navigation', () => false)

  // Functions
  const openDrawer = (options: DrawerContent) => {
    const { component, data, title } = options
    const newContent: DrawerContent = { component, data, title }

    // If drawer is already open and we're opening a different member/entity
    if (isDrawerOpen.value && !isInternalNavigation.value) {
      const isDifferentEntity = entityId(drawerContent.value?.data) !== entityId(data)

      if (isDifferentEntity) {
        // Opening a different entity from outside - reset history completely
        drawerContent.value = newContent
        drawerTitle.value = title || ''
        drawerHistory.value = [newContent]
      }
      else {
        // Same entity - just update the content
        drawerContent.value = newContent
        drawerTitle.value = title || ''
      }
    }
    else {
      // Either first open or internal navigation - keep history
      drawerContent.value = newContent
      drawerTitle.value = title || ''

      // Add to history if we're navigating internally or first opening
      if (isInternalNavigation.value || drawerHistory.value.length === 0) {
        // Prevent duplicate history entries
        const last = drawerHistory.value[drawerHistory.value.length - 1]
        if (!last || entityId(last.data) !== entityId(data)) {
          drawerHistory.value.push(newContent)
        }
      }
    }

    // Reset internal navigation flag after use
    isInternalNavigation.value = false
    isDrawerOpen.value = true
  }

  const closeDrawer = () => {
    isDrawerOpen.value = false
    // Clear history when closing
    drawerHistory.value = []
    isInternalNavigation.value = false
  }

  const goBackInDrawer = () => {
    // Remove current item
    drawerHistory.value.pop()

    // Get the previous item
    const previousContent = drawerHistory.value[drawerHistory.value.length - 1]

    if (previousContent) {
      isInternalNavigation.value = true
      drawerContent.value = previousContent
      drawerTitle.value = previousContent.title || ''
    }
    else {
      closeDrawer()
    }
  }

  // Modify resetDrawerHistory to clear the flag too
  const resetDrawerHistory = () => {
    // Clear history except for the current item
    const current = drawerContent.value
    drawerHistory.value = current ? [current] : []
    isInternalNavigation.value = false
  }

  // Reset function for testing and initialization
  const resetDrawerState = () => {
    isDrawerOpen.value = false
    drawerTitle.value = ''
    drawerContent.value = null
    drawerHistory.value = []
    isInternalNavigation.value = false
  }

  const updateDrawerTitle = (newTitle: string) => {
    // Update the current title
    drawerTitle.value = newTitle

    // Also update the title in the current drawer history item
    const currentDrawer = drawerHistory.value[drawerHistory.value.length - 1]
    if (currentDrawer) {
      currentDrawer.title = newTitle
    }
  }

  // Compute if we can go back
  const canGoBack = computed(() => drawerHistory.value.length > 1)

  return {
    isDrawerOpen,
    drawerTitle,
    drawerContent,
    drawerHistory,
    canGoBack,
    isInternalNavigation,
    openDrawer,
    closeDrawer,
    goBackInDrawer,
    resetDrawerHistory,
    // Export this so components can set it before navigation
    setInternalNavigation: (value: boolean) => {
      isInternalNavigation.value = value
    },
    updateDrawerTitle,
    resetDrawerState, // Export this for testing
  }
}
